import { FC, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import Checkbox from "@/components/shared/Checkbox"
import ExportRow, { ExportRange } from "@/components/shared/ExportRow"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import SnackBar from "@/components/shared/SnackBar"
import { useExportRequest } from "@/hooks/useExportRequest"
import { useUserRole } from "@/hooks/useUserRole"
import { AppDispatch, RootState } from "@/store"
import { selectTimezone } from "@/store/facilitiesSlice"
import { exportLevelData, exportLevelLogs } from "@/store/facilityLevelsSlice"
import { Feature } from "@/types/user"
import { getSensor } from "@/utils/sensors"
import "./index.scss"

const SNACKBAR_DURATION = 8000
const DATE_FORMAT = "YYYY-MM-DD"

const Export: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const { id } = useParams<{ id: string }>()
   const canAccessExport = useUserRole(Feature.EXPORT)
   const { facilityLevel, loadingLevelExport, loadingLevelLogsExport } =
      useSelector(
         ({ facilityLevelsReducer }: RootState) => facilityLevelsReducer
      )
   const { sensors } = facilityLevel

   const cellSensor = useMemo(() => getSensor(sensors), [sensors])
   const timezone = useSelector(selectTimezone)

   const [alarmsOnly, setAlarmsOnly] = useState(false)
   const { snackbarMessage, closeSnackbar, requestExport } = useExportRequest()

   if (!canAccessExport || !timezone) return null

   const handleRequestReport = ({ startDate, endDate }: ExportRange) => {
      if (!id || !cellSensor) return
      requestExport(
         () =>
            dispatch(
               exportLevelData({
                  level: id,
                  sensor: cellSensor.id,
                  startDate: startDate.tz(timezone).format(DATE_FORMAT),
                  endDate: endDate.tz(timezone).format(DATE_FORMAT),
                  timezone,
               })
            ).unwrap(),
         "report"
      )
   }

   const handleRequestLogs = ({ startDate, endDate }: ExportRange) => {
      if (!id) return
      requestExport(
         () =>
            dispatch(
               exportLevelLogs({
                  level: id,
                  startDate: startDate.tz(timezone).format(DATE_FORMAT),
                  endDate: endDate.tz(timezone).format(DATE_FORMAT),
                  timezone,
                  alarmsOnly,
               })
            ).unwrap(),
         "logs"
      )
   }

   return (
      <Grid className="cell-detail-overview__export">
         <GridItem size={12}>
            {!cellSensor ? (
               <div className="export__row-description">
                  This cell does not have a radar sensor, so the data report is
                  not available.
               </div>
            ) : (
               <ExportRow
                  title="Request a report"
                  description="Select a date range. Your report will be prepared and sent to your email."
                  timezone={timezone}
                  loading={loadingLevelExport}
                  onRequest={handleRequestReport}
               />
            )}
         </GridItem>
         <GridItem size={12}>
            <ExportRow
               title="Request logs"
               description="Select a date range. All logs are included unless you select alarms only. Your logs will be prepared and sent to your email."
               timezone={timezone}
               loading={loadingLevelLogsExport}
               onRequest={handleRequestLogs}
               extraControls={
                  <Checkbox
                     id="export-alarms-only"
                     label="Only alarms"
                     checked={alarmsOnly}
                     onChange={setAlarmsOnly}
                  />
               }
            />
         </GridItem>
         <SnackBar
            isOpen={!!snackbarMessage}
            message={snackbarMessage}
            duration={SNACKBAR_DURATION}
            onClose={closeSnackbar}
         />
      </Grid>
   )
}

export default Export
